import React, {
    useState, useEffect, useCallback, useContext, useMemo
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import {BreakpointTrackerContext, withBreakpoint} from "@jutro/layout";
import { withRouter } from 'react-router-dom';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication } from 'gw-digital-auth-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import {
    AcceptWorkActionPopup,
    CommonWorkActionPopup,
    AddQuoteModal,
    DelayWorkActionPopup,
    AddInvoicePopup,
    WithdrawInvoicePopup,
    ReplyMessagePopup,
    CreateNewMessagePopup
} from 'gw-capability-servicerequest-react';
import {
    Loader, ModalNextProvider, FormattedDate, Button, DateField, DateValue
} from '@jutro/components';
import { Link, Currency as CurrencyField } from 'gw-components-platform-react';
import metadata from './ServiceRequestDetailsPage.metadata.json5';
import styles from './ServiceRequestDetailsPage.module.scss';
import messages from './ServiceRequestDetailsPage.messages';
import { getMetadataByBreakPoint } from "../../customer/utils/breakpointUtil";

function ServiceRequestDetailsPage(props) {
    const { match } = props;
    const { params } = match;
    const { serviceRequestNumber } = params;
    const { authHeader } = useAuthentication();
    const [isLoading, setLoadingState] = useState(false);
    const [serviceItems, setServiceItems] = useState({});
    const [serviceDetails, setServiceDetails] = useState({ otherOperations: [] });
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);

    const PRIMARY_PHONE_TYPE_MAP = {
        home: 'homeNumber',
        mobile: 'cellNumber',
        work: 'workNumber'
    };

    const readValue = useCallback((id, path) => {
        if (path === 'otherOperations') {
            return _.get(serviceDetails, path);
        }
        return _.get(serviceItems, path);
    }, [serviceDetails, serviceItems]);

    const lookupActionNameMessageKey = useCallback(
        (action) => {
            const { isReviseQuote } = serviceItems;
            const { quoteExists } = serviceDetails;
            if (action) {
                if (action === 'addquote') {
                    if (isReviseQuote || quoteExists) {
                        return translator(messages.actionReviseQuote);
                    }
                    return translator(messages.actionAddQuote);
                }
                if (action === 'vendordelayed') { // not an operation in CC, but an available action
                    return translator(messages.actionReportADelay);
                }
                return translator(messages[action]);
            }
            return null;
        }, [serviceDetails, serviceItems, translator]
    );

    const getServiceRequestDetails = useCallback(
        () => {
            setLoadingState(true);
            ServiceRequestService.getServiceRequestDetails(serviceRequestNumber, authHeader)
                .then((response) => {
                    const serviceData = {};
                    setServiceItems(response);

                    serviceData.isOverdue = response.isOverdue;
                    serviceData.quoteExists = !_.isUndefined(response.latestQuote);
                    const hasDefaultOperation = !_.isUndefined(response.defaultOperation);
                    serviceData.firstOperation = hasDefaultOperation
                        ? response.defaultOperation
                        : _.head(response.otherOperations);
                    serviceData.otherOperations = hasDefaultOperation
                        ? response.otherOperations
                        : _.tail(response.otherOperations);
                    serviceData.dueDate = response.serviceDateApplies
                        ? response.expectedServiceCompletionDate
                        : response.expectedQuoteCompletionDate;

                    const customerContactPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.customerContact.primaryPhoneType);
                    const claimHandlerPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.claimHandler.primaryPhoneType);
                    serviceData.customerContactPreferredNumber = _.get(response.customerContact,
                        customerContactPreferredNumberType);
                    serviceData.claimHandlerPreferredNumber = _.get(response.claimHandler,
                        claimHandlerPreferredNumberType);

                    serviceData.invoiceTotal = {
                        amount: response.outstandingInvoiceAmount.amount
                            + response.paymentsMadeAmount.amount,
                        currency: response.outstandingInvoiceAmount.currency
                    };
                    serviceData.sortedHistory = _.sortBy(response.history, 'sequence');
                    if (response.additionalInstructions
                            && response.additionalInstructions.length > 100) {
                        serviceData.showFullInstructions = false;
                        serviceData.truncatedInstructions = _.truncate(
                            response.additionalInstructions, { length: 100 }
                        );
                    }
                    if (response.canDelay) {
                        serviceData.otherOperations.push('vendordelayed'); // Not a real SR Operation - but needed to reflect ability to delay date in CC
                    }

                    setServiceDetails(serviceData);
                })
                .finally(() => {
                    setLoadingState(false);
                });
        }, [PRIMARY_PHONE_TYPE_MAP, authHeader, serviceRequestNumber]
    );

    const handleAcceptWork = useCallback(
        (actionData) => {
            ServiceRequestService.acceptWork(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCompleteWork = useCallback(
        () => {
            ServiceRequestService.completeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const handleDeclineWork = useCallback(
        (actionData) => {
            ServiceRequestService.declineWork(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCancelWork = useCallback(
        (actionData) => {
            ServiceRequestService.cancelServiceRequest(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handlePauseWork = useCallback(
        (actionData) => {
            ServiceRequestService.reportBlocked(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleDelayWork = useCallback(
        (actionData) => {
            ServiceRequestService.reportDelay(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleAddQuote = useCallback(
        (quoteVM) => {
            ServiceRequestService.addOrReplaceQuote(
                serviceItems.publicID, quoteVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceItems]
    );

    const handleAddInvoice = useCallback(
        (invoiceVM) => {
            ServiceRequestService.addInvoice(
                serviceItems.publicID, invoiceVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceItems]
    );

    const handleWithdrawInvoice = useCallback(
        (withdrawInvoiceVM) => {
            ServiceRequestService.withdrawInvoice(
                withdrawInvoiceVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleResumeWork = useCallback(
        () => {
            ServiceRequestService.resumeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const performOperation = useCallback(
        (otherOperation) => {
            let actionProps = {
                serviceRequest: serviceItems,
                viewModelService: viewModelService
            };
            switch (otherOperation) {
                case 'specialistacceptedwork':
                    ModalNextProvider.showModal(
                        <AcceptWorkActionPopup {...actionProps} />
                    )
                        .then(handleAcceptWork)
                        .catch(_.noop);
                    break;
                case 'specialistcompletedwork':
                    ModalNextProvider.showConfirm({
                        title: translator(messages.actionTitleCompleteWork),
                        message: translator(messages.actionMessageCompleteWork),
                        confirmButtonText: translator(messages.actionButtonCompleteWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleCompleteWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                case 'specialistdeclined':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitleDeclineWork),
                        modalMessage: translator(messages.actionMessageDeclineWork),
                        defaultButtonLabel: translator(messages.actionButtonDeclineWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handleDeclineWork)
                        .catch(_.noop);
                    break;
                case 'specialistcanceled':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitleCancelWork),
                        modalMessage: translator(messages.actionMessageCancelWork),
                        defaultButtonLabel: translator(messages.actionButtonCancelWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handleCancelWork)
                        .catch(_.noop);
                    break;
                case 'specialistwaiting':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitlePauseWork),
                        modalMessage: translator(messages.actionMessagePauseWork),
                        defaultButtonLabel: translator(messages.actionButtonPauseWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handlePauseWork)
                        .catch(_.noop);
                    break;
                case 'addquote':
                    ModalNextProvider.showModal(
                        <AddQuoteModal
                            quoteValue={serviceItems.latestQuote}
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                        />
                    )
                        .then(handleAddQuote)
                        .catch(_.noop);
                    break;
                case 'addinvoice':
                    ModalNextProvider.showModal(
                        <AddInvoicePopup
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                        />
                    )
                        .then(handleAddInvoice)
                        .catch(_.noop);
                    break;
                case 'vendordelayed':
                    ModalNextProvider.showModal(
                        <DelayWorkActionPopup {...actionProps} />
                    )
                        .then(handleDelayWork)
                        .catch(_.noop);
                    break;
                case 'specialistresumedwork':
                    ModalNextProvider.showConfirm({
                        title: translator(messages.actionTitleResumeWork),
                        message: translator(messages.actionMessageResumeWork),
                        confirmButtonText: translator(messages.actionButtonResumeWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleResumeWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                default:
                    break;
            }
        }, [
            handleAcceptWork,
            handleAddQuote,
            handleAddInvoice,
            handleCompleteWork,
            handleDeclineWork,
            handlePauseWork,
            handleDelayWork,
            handleCancelWork,
            handleResumeWork,
            serviceItems,
            translator,
            viewModelService
        ]
    );

    const generateActionItemOverrides = useMemo(
        () => {
            const { otherOperations } = serviceDetails;
            const actionOverrides = otherOperations.map((otherOperation, index) => {
                return {
                    [`actionItem${index}`]: {
                        content: lookupActionNameMessageKey(otherOperation),
                        onClick: () => performOperation(otherOperation)
                    }
                };
            });
            return Object.assign({}, ...actionOverrides);
        }, [lookupActionNameMessageKey, performOperation, serviceDetails]
    );

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const renderEmailLink = useCallback((emailID) => {
        return (
            <Link className={styles.link} onClick={(e) => mailToTrigger(e, emailID)} href="/">
                {emailID}
            </Link>
        );
    }, [mailToTrigger]);

    const phoneTrigger = useCallback((event, phoneNumber) => {
        event.preventDefault();
        window.location = `tel:${phoneNumber}`;
    }, []);

    const getDateModified = useCallback((items, index, { path: property }) => {
        if (typeof items[property] === 'undefined') {
            return '';
        }
        // return <FormattedDate value={new Date(items[property])} format="short" />;

        return (
            <DateValue
                format="short"
                placeholder="-"
                showTime={false}
                value={items[property]}
            />
        );
    }, []);

    const renderPhoneLink = useCallback((phone) => {
        return (
            <Link className={styles.link} onClick={(e) => phoneTrigger(e, phone)} href="/">
                {phone}
            </Link>
        );
    }, [phoneTrigger]);

    const getCellPremium = useCallback((rowData, rowIndex) => {
        return (
            <CurrencyField
                id={`totalamount_${rowIndex}`}
                value={rowData.totalAmount}
                readOnly
            />
        );
    }, []);

    const addNewMessage = useCallback(() => {
        ModalNextProvider.showModal(
            <CreateNewMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
            />
        ).then(getServiceRequestDetails);
    }, [authHeader, getServiceRequestDetails, serviceRequestNumber]);

    const replyMessage = useCallback((messageData) => {
        ModalNextProvider.showModal(
            <ReplyMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                serviceItems={serviceItems}
                messageData={messageData}
            />
        ).then(getServiceRequestDetails);
    }, [getServiceRequestDetails, serviceItems, serviceRequestNumber]);

    const getInvoiceStatus = useCallback((rowData) => {
        return translator({
            id: `typekey.ServiceRequestInvoiceStatus.${rowData.status}`,
            defaultMessage: rowData.status
        });
    }, [translator]);

    const withdrawInvoice = useCallback((invoiceData) => {
        ModalNextProvider.showModal(
            <WithdrawInvoicePopup invoiceID={invoiceData.publicID} />
        ).then(handleWithdrawInvoice);
    }, [handleWithdrawInvoice]);

    const getDocumentDownloadLink = useCallback(
        (item) => {
            const { publicID, sessionID } = item;
            const linkHref = ServiceRequestService.downloadDocumentLink(publicID, sessionID);
            return (
                <Link className={styles.link} href={linkHref}>
                    {item.name}
                </Link>
            );
        },
        []
    );

    const addNewInvoice = useCallback(() => {
        ModalNextProvider.showModal(
            <AddInvoicePopup
                serviceRequestID={serviceItems.publicID}
                existingDocuments={serviceItems.documents}
            />
        ).then(handleAddInvoice);
    }, [handleAddInvoice, serviceItems]);

    const getWithdrawButton = useCallback((invoiceData) => {
        if (!invoiceData.canWithdraw) {
            return null;
        }

        return (
            <Button
                id="withdrawButton"
                type="secondary"
                icon="mi-delete"
                onClick={() => withdrawInvoice(invoiceData)}
            />
        );
    }, [withdrawInvoice]);

    const getPrimaryPhone = (contact) => {
        if (contact) {
            return contact.primaryPhoneType === 'home' ? contact.homeNumber : (contact.primaryPhoneType === 'work' ? contact.workNumber : contact.cellNumber);
        }
    };

    useEffect(() => {
        getServiceRequestDetails();
        // disable re render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const override = {
        '@field': {
            labelPosition: 'left'
        },
        serviceRequestsDetailsTitle: {
            content: `${_.get(serviceItems, 'customerContact.displayName')} - ${_.get(serviceItems, 'claimID')}`
        },
        customerContactEmail: {
            content: renderEmailLink(_.get(serviceItems, 'customerContact.emailAddress1'))
        },
        claimHandlerEmail: {
            content: renderEmailLink(_.get(serviceItems, 'claimHandler.emailAddress1'))
        },
        claimHandlerContact: {
            //content: renderPhoneLink(_.get(serviceItems, 'claimHandler.cellNumber'))
            content: renderPhoneLink(_.get(getPrimaryPhone(serviceItems.claimHandler)))
        },
        customerContactNumber: {
            //content: renderPhoneLink(_.get(serviceItems, 'customerContact.homeNumber'))
            content: renderPhoneLink(getPrimaryPhone(serviceItems.customerContact))
        },
        actionsDropdown: {
            visible: serviceDetails.otherOperations.length > 0
        },
        addInvoiceButtonContainer: {
            visible: _.get(serviceItems, 'canAddInvoice')
        },
        actionDefaultButton: {
            content: lookupActionNameMessageKey(serviceDetails.firstOperation),
            onClick: () => performOperation(serviceDetails.firstOperation),
            visible: !_.isUndefined(serviceDetails.firstOperation)
        },
        ...generateActionItemOverrides,
        messageTable: {
            data: _.get(serviceItems, 'messages'),
            onRowClick: replyMessage
        },
        invoiceTable: {
            data: _.get(serviceItems, 'invoices')
        },
        historyTable: {
            data: _.get(serviceItems, 'history')
        },
        documentTable: {
            data: _.get(serviceItems, 'documents')
        },
        invoiceTotalAmountColumn: {
            onCell: getCellPremium
        },
        invoiceStatusColumn: {
            onCell: getInvoiceStatus
        },
        documentName: {
            onCell: getDocumentDownloadLink
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            getDateModified: getDateModified,
            addNewInvoice: addNewInvoice,
            addNewMessage: addNewMessage,
            getWithdrawButton: getWithdrawButton
        }
    };

    return renderContentFromMetadata(getMetadataByBreakPoint(metadata, breakpoint), override, resolvers);
}

ServiceRequestDetailsPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            serviceRequestNumber: PropTypes.string
        })
    }).isRequired
};

export default withRouter(ServiceRequestDetailsPage);
//export default withBreakpoint(withRouter(ServiceRequestDetailsPage));
